import React from "react";
import { injectIntl, IntlShape } from "@ai360/core/intl";
import { config as intlConfig } from "~/intl-provider/config";
import { connect } from "react-redux";

import {
    Bucket,
    BucketHeader,
    Checkbox,
    DialogBox,
    DialogBoxFooterType,
    Loader,
    NumericInput,
    RadioButtonGroup,
    RadioButton,
    SelectInput,
    SimpleTable,
    NoLink,
} from "~/core";
import { getTheUserGuid } from "~/login";
import { actions as notificationActions } from "~/notifications";
import { ISelectOption } from "~/core/components/select-input/model";

import {
    getEquipmentGuidToHarvestLoadListMap,
    getYieldCalibrationPassedValidation,
    getYieldCalibrationOptions,
    getYieldCalibrationErrorState,
} from "~/recs-events/events/selectors";
import { actions as picklistActions, selectors as picklistSelectors } from "~/core/picklist";
import { getPickListCode, PICKLIST_CROP_PURPOSE } from "~/core/picklist/picklist-names";
import * as eventActions from "~/recs-events/events/actions";
import * as eventListActions from "../../actions";
import { getYieldCalibrationLoading, getFieldGuidToSelectedEventGuidSetMap } from "../../selectors";
import { getEventSummary } from "../../../event-info/selectors";
import { messages } from "../../../i18n-messages";

import "./yield-calibration.css";
import { AgEventAPI, FormattingHelpers, PicklistAPI, SearchAPI } from "@ai360/core";
import { AgEventSummary } from "~/recs-events/events/models";

const WHOLE_CROP_PURPOSE_NAMES: ReadonlySet<string> = new Set([
    "Hay",
    "Silage",
    "Green Manure",
    "Biomass",
    "Forage",
]);

const blank = "-";
const percent = "%";
const AVG_DRY_YIELD = 1;
const PER_ACRE = "/ac";

enum RequestType {
    Apply = 1,
    Reset = 3,
}
interface ICalibrationRequest {
    mode: RequestType;
    loads: AgEventAPI.ICalibrateHarvestLoad[];
    moistureAdjustPercent?: number;
    moistureOverride?: boolean;
    yieldAdjustTypeId?: number;
    yieldAdjustValue?: number;
}
interface IYieldCalibrationModalProps {
    saveYieldCalibration: (batch: ICalibrationRequest[], agEventGuidList: string[]) => null;
    equipmentGuidToHarvestLoadListMap: Map<string, AgEventAPI.ICalibrateEquipmentLoads>; //Map
    eventSummary: AgEventSummary;
    fieldEventMap: Map<string, Set<string>>;
    intl: IntlShape;
    isLoading: boolean;
    isOpen: boolean;
    onApiError: (err, message) => null;
    onClose: () => null;
    passedYieldCalibrationValidation: boolean;
    userGuid: string;
    yieldCalibrationPicklist: PicklistAPI.IPicklistValue[];
    yieldCalibrationHasError: boolean;
    clearYieldCalibrationError: () => null;
    clearYieldCalibrationLoadList: () => null;
    setEquipmentGuidToHarvestLoadListMap: (
        newMap: Map<string, AgEventAPI.ICalibrateEquipmentLoads>
    ) => null;
    fetchPicklistData: (picklists: Record<string, number>) => void;
    wholeCropPurposeGuids: Set<string>;
}
interface IYieldCalibrationModalState {
    confirmReset: boolean;
    errorsToDisplay: number[];
    isCalibrateToYield: string | boolean;
    isOverrideMoisture: boolean;
    modifiedLoadList: AgEventAPI.ICalibrateHarvestLoad[];
    moisture?: number;
    requests: ICalibrationRequest[];
    resetInvalid: boolean;
    title: string;
    yieldAmount?: number;
    yieldCalibrationAdjustId?: number;
}

interface IRecord {
    label: string;
    original: string;
    calibrated: string;
}

interface IRecordsData {
    calculatedYieldAveragesRecords: IRecord[];
    cropName: string;
    cropPurposeName: string;
    totalYieldAmount: number;
    totalAreaAmount: number;
    yieldUnitName: string;
}

class YieldCalibrationModal_ extends React.Component<
    IYieldCalibrationModalProps,
    IYieldCalibrationModalState
> {
    constructor(props: IYieldCalibrationModalProps) {
        super(props);
        this.state = {
            confirmReset: false,
            errorsToDisplay: [],
            isCalibrateToYield: true,
            isOverrideMoisture: false,
            modifiedLoadList: [],
            moisture: -1,
            requests: [],
            resetInvalid: false,
            title: "",
            yieldAmount: -1,
            yieldCalibrationAdjustId: null,
        };
    }

    private isWholeCropLoad(load: AgEventAPI.ICalibrateHarvestLoad): boolean {
        return this.props.wholeCropPurposeGuids.has(load.cropPurposeGuid);
    }

    private isCropPurposeCompatible(
        newLoad: AgEventAPI.ICalibrateHarvestLoad,
        existingList: AgEventAPI.ICalibrateHarvestLoad[]
    ): boolean {
        if (existingList.length === 0) return true;
        const newIsWhole = this.isWholeCropLoad(newLoad);
        const existingIsWhole = this.isWholeCropLoad(existingList[0]);
        if (newIsWhole && existingIsWhole) return true;
        if (!newIsWhole && !existingIsWhole)
            return !existingList.some((l) => l.cropPurposeName !== newLoad.cropPurposeName);
        return false;
    }

    private getDefaultRecordsData(): IRecordsData {
        const { formatMessage } = this.props.intl;
        const { modifiedLoadList } = this.state;
        const isCotton = modifiedLoadList.length > 0 && modifiedLoadList[0].lintMode;

        return {
            calculatedYieldAveragesRecords: [
                {
                    label: formatMessage(
                        !isCotton
                            ? messages.yieldCalibrationAvgDryYield
                            : messages.yieldCalibrationAvgLintYield
                    ),
                    original: blank,
                    calibrated: blank,
                },
                {
                    label: formatMessage(
                        !isCotton
                            ? messages.yieldCalibrationAvgMoisture
                            : messages.yieldCalibrationAvgLintPct
                    ),
                    original: blank,
                    calibrated: blank,
                },
            ],
            cropName: "",
            cropPurposeName: "",
            totalYieldAmount: 0,
            totalAreaAmount: 0,
            yieldUnitName: "",
        };
    }

    private applyYieldCalibration() {
        const { equipmentGuidToHarvestLoadListMap, setEquipmentGuidToHarvestLoadListMap } =
            this.props;
        const {
            isCalibrateToYield,
            isOverrideMoisture,
            moisture,
            requests,
            yieldAmount,
            yieldCalibrationAdjustId,
            modifiedLoadList,
        } = this.state;

        const yieldCalibrationModel: ICalibrationRequest = {
            mode: RequestType.Apply,
            loads: modifiedLoadList,
            moistureOverride: false,
            moistureAdjustPercent: null,
            yieldAdjustTypeId: null,
            yieldAdjustValue: null,
        };
        if (!isCalibrateToYield && moisture > -1) {
            yieldCalibrationModel.moistureAdjustPercent = moisture;
            yieldCalibrationModel.moistureOverride = isOverrideMoisture;
        }
        if (isCalibrateToYield && yieldAmount > -1) {
            yieldCalibrationModel.yieldAdjustTypeId = yieldCalibrationAdjustId;
            yieldCalibrationModel.yieldAdjustValue = yieldAmount;
        }

        const totalAcres = modifiedLoadList.reduce((acc, load) => {
            acc += load.loadArea;
            return acc;
        }, 0);
        let totalYield = 0;
        const totalAverageDryYield = modifiedLoadList.reduce((acc, load) => {
            acc +=
                (load.averageCalibratedDryYield || load.averageDryYield) *
                (load.loadArea / totalAcres);
            totalYield += (load.averageCalibratedDryYield || load.averageDryYield) * load.loadArea;
            return acc;
        }, 0);

        const totalAveragePercent = modifiedLoadList.reduce((acc, load) => {
            acc +=
                (load.averageCalibratedPercent || load.averagePercent) *
                (load.loadArea / totalAcres);
            return acc;
        }, 0);

        // Go through and mangle the equipmentGuidToHarvestLoadListMap such that it looks like the aftermath of the requested action
        const newEquipmentGuidToHarvestLoadListMap = new Map(equipmentGuidToHarvestLoadListMap);
        for (const [epg, item] of equipmentGuidToHarvestLoadListMap) {
            const { customerFieldList } = item;
            const newCustomerFieldList = [];

            for (const fieldItem of customerFieldList) {
                const newLoadList = [];

                for (const loadItem of fieldItem.loadList) {
                    const isMatch = modifiedLoadList.some(
                        (l) =>
                            l.agEventGuid === loadItem.agEventGuid &&
                            l.equipmentProfileGuid === loadItem.equipmentProfileGuid &&
                            l.loadName === loadItem.loadName
                    );

                    if (isMatch) {
                        if (isCalibrateToYield) {
                            let percentFactor = 1;
                            switch (yieldCalibrationAdjustId) {
                                case 1: // Avg Dry Yield
                                    percentFactor = yieldAmount / totalAverageDryYield;
                                    break;
                                case 2: // % Increase
                                    percentFactor += yieldAmount / 100;
                                    break;
                                case 3: // % Decrease
                                    percentFactor -= yieldAmount / 100;
                                    break;
                                default: // 4: Total Yield
                                    percentFactor = yieldAmount / totalYield;
                                    break;
                            }

                            loadItem.totalDryYield *= percentFactor;
                            loadItem.averageCalibratedDryYield *= percentFactor;
                            loadItem.averageCalibratedWetYield *= percentFactor;
                        } else {
                            // calibrating for moisture percent
                            loadItem.averageCalibratedPercent = this.state.isOverrideMoisture
                                ? Number(moisture)
                                : (Number(moisture) / totalAveragePercent) *
                                  loadItem.averageCalibratedPercent;

                            // Whole-crop purposes (e.g. silage) don't derive yield from moisture
                            if (!this.isWholeCropLoad(loadItem)) {
                                const effectiveMoisturePct = loadItem.lintMode
                                    ? Number(moisture) / 100
                                    : (1 - Number(moisture) / 100) /
                                      (1 - loadItem.cropOptimalPercent / 100);

                                loadItem.averageCalibratedDryYield =
                                    loadItem.averageCalibratedWetYield * effectiveMoisturePct;
                                loadItem.totalDryYield =
                                    loadItem.averageCalibratedDryYield * loadItem.loadArea;
                            }
                        }
                    }

                    newLoadList.push(loadItem);
                }
                newCustomerFieldList.push({
                    ...fieldItem,
                    loadList: newLoadList,
                });
            }
            newEquipmentGuidToHarvestLoadListMap.set(epg, {
                ...item,
                customerFieldList: newCustomerFieldList,
            });
        }

        this.setState(
            {
                requests: [...requests, yieldCalibrationModel],
            },
            () => setEquipmentGuidToHarvestLoadListMap(newEquipmentGuidToHarvestLoadListMap)
        );
    }

    private saveYieldCalibration() {
        const { requests } = this.state;
        const agEventGuidList = new Set<string>();
        requests.forEach((req) =>
            req.loads.forEach((load) => agEventGuidList.add(load.agEventGuid))
        );

        this.setState(
            {
                modifiedLoadList: [],
                requests: [],
            },
            () => {
                this.props.saveYieldCalibration(requests, Array.from(agEventGuidList));
                this.props.clearYieldCalibrationLoadList();
                this.onClose();
            }
        );
    }

    private emptyFieldMap(): boolean {
        const { fieldEventMap } = this.props;
        return fieldEventMap.size < 1 || [...fieldEventMap.values()].every((s) => s.size < 1);
    }

    private async getModalTitle(): Promise<string> {
        const { formatMessage, formatNumber } = this.props.intl;
        const { eventSummary, fieldEventMap, userGuid } = this.props;

        let fieldGuidsWithTargetEvents: string[] = [];
        if (this.emptyFieldMap()) {
            //No Events were selected, so use the target Event Field
            fieldGuidsWithTargetEvents = [eventSummary.fieldGuid];
        } else {
            const fieldsWithSelectedEvents = new Map(
                [...fieldEventMap].filter((v) => v[1].size > 0)
            );
            fieldGuidsWithTargetEvents = [...fieldsWithSelectedEvents.keys()];
        }

        const fields = await SearchAPI.getFields({
            fieldGuid: fieldGuidsWithTargetEvents,
            userGuid,
        });

        const totalAcres = fields.reduce((acc, x) => acc + x.acres, 0);

        const fieldsLabel =
            fieldGuidsWithTargetEvents.length > 1
                ? messages.fieldsLabel.defaultMessage
                : messages.fieldLabel.defaultMessage;
        const formattedAcres = formatNumber(totalAcres, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        return `${
            formatMessage(messages.yieldCalibrationTitle) +
            " (" +
            fieldGuidsWithTargetEvents.length +
            " " +
            fieldsLabel +
            ", " +
            formattedAcres +
            " ac)"
        }`;
    }

    private getYieldUnitName(harvestLoadList: AgEventAPI.ICalibrateHarvestLoad[] = []) {
        return harvestLoadList.reduce((acc, { yieldUnitName }, index) => {
            if (index) {
                console.assert(
                    acc === yieldUnitName,
                    "Yield Unit Name is not same for the harvestLoadList",
                    { acc, yieldUnitName }
                );
            }
            return yieldUnitName;
        }, "");
    }

    private setRecordsData(
        recordsData: IRecordsData,
        checkedHarvestLoadList: AgEventAPI.ICalibrateHarvestLoad[],
        {
            averageDryYield,
            averagePercent,
            averageCalibratedDryYield,
            averageCalibratedPercent,
        }: {
            averageDryYield: number;
            averagePercent: number;
            averageCalibratedDryYield: number;
            averageCalibratedPercent: number;
        }
    ) {
        const yieldUnitName = this.getYieldUnitName(checkedHarvestLoadList);
        recordsData.calculatedYieldAveragesRecords[0].original = this.formatAvgYield(
            recordsData.calculatedYieldAveragesRecords[0].original,
            averageDryYield,
            yieldUnitName
        );
        recordsData.calculatedYieldAveragesRecords[1].original = this.formatPercentData(
            recordsData.calculatedYieldAveragesRecords[1].original,
            averagePercent,
            percent
        );

        recordsData.calculatedYieldAveragesRecords[0].calibrated = this.formatAvgYield(
            recordsData.calculatedYieldAveragesRecords[0].calibrated,
            averageCalibratedDryYield,
            yieldUnitName
        );
        recordsData.calculatedYieldAveragesRecords[1].calibrated = this.formatPercentData(
            recordsData.calculatedYieldAveragesRecords[1].calibrated,
            averageCalibratedPercent,
            percent
        );

        return recordsData;
    }

    private getYieldUnit(checkedHarvestLoadList: AgEventAPI.ICalibrateHarvestLoad[]): string {
        const item = checkedHarvestLoadList.find((l) => !!l.yieldUnitName);
        return !item ? "" : item.yieldUnitName;
    }

    private calculateYieldAverages(checkedHarvestLoadList: AgEventAPI.ICalibrateHarvestLoad[]) {
        const recordsData = this.getDefaultRecordsData();
        if (checkedHarvestLoadList.length === 0) {
            return recordsData;
        }
        let averageDryYield = 0,
            averagePercent = 0,
            averageCalibratedDryYield = 0,
            averageCalibratedPercent = 0,
            totalDryYield = 0;

        recordsData.yieldUnitName = this.getYieldUnit(checkedHarvestLoadList);

        const combAcres = checkedHarvestLoadList.reduce((acc, load) => {
            acc += load.loadArea;
            return acc;
        }, 0);
        let anyCalibrated = false;
        const cropPurposeNames = new Set<string>();

        checkedHarvestLoadList.forEach((loadItem) => {
            anyCalibrated = anyCalibrated || this.isLoadCalibrated(loadItem);
            let weightedAverageFactor = 0;
            if (combAcres > 0) {
                weightedAverageFactor = loadItem.loadArea / combAcres;
            }

            recordsData.cropName = loadItem.cropName;
            if (loadItem.cropPurposeName) {
                cropPurposeNames.add(loadItem.cropPurposeName);
            }
            const currentDryYield = loadItem.averageDryYield * weightedAverageFactor;
            const currentPercent = loadItem.averagePercent * weightedAverageFactor;
            const currentTotalYield = loadItem.totalDryYield;
            averageDryYield = this.calculateNewAverage(averageDryYield, currentDryYield);
            averagePercent = this.calculateNewAverage(averagePercent, currentPercent);
            totalDryYield = this.calculateNewAverage(totalDryYield, currentTotalYield);

            const currentCalibratedDryYield =
                (loadItem.averageCalibratedDryYield || loadItem.averageDryYield) *
                weightedAverageFactor;
            const currentCalibratedPercent =
                (loadItem.averageCalibratedPercent || loadItem.averagePercent) *
                weightedAverageFactor;
            averageCalibratedDryYield = this.calculateNewAverage(
                averageCalibratedDryYield,
                currentCalibratedDryYield
            );
            averageCalibratedPercent = this.calculateNewAverage(
                averageCalibratedPercent,
                currentCalibratedPercent
            );
        });
        if (!anyCalibrated) {
            averageCalibratedDryYield = 0;
            averageCalibratedPercent = 0;
        }
        recordsData.cropPurposeName = Array.from(cropPurposeNames).join(", ");
        recordsData.totalAreaAmount = combAcres;
        recordsData.totalYieldAmount = totalDryYield;

        return this.setRecordsData(recordsData, checkedHarvestLoadList, {
            averageDryYield,
            averagePercent,
            averageCalibratedDryYield,
            averageCalibratedPercent,
        });
    }

    // Formats the average value to be displayed on the table
    private formatPercentData(defaultVal: string, value: number, yieldUnitName: string) {
        const { formatNumber } = this.props.intl;
        if (value && value > 0) {
            return `${formatNumber(value, intlConfig.numberFormatOptions)} ${yieldUnitName}`;
        }
        return defaultVal;
    }

    private formatAvgYield(defaultVal: string, value: number, yieldUnitName: string): string {
        const { formatNumber } = this.props.intl;
        if (!value) {
            return defaultVal;
        }
        const decimals = FormattingHelpers.progressiveRoundingDecimals(Number(value));
        return `${formatNumber(value, intlConfig.customFormatOptions(decimals))} ${yieldUnitName}`;
    }

    private calculateNewAverage(original: number, recordAverage: number) {
        if (!recordAverage) {
            return original;
        }
        return original + recordAverage;
    }

    private createSelectedLoadsCheckboxes(
        equipmentGuidToHarvestLoadListMap: Map<string, AgEventAPI.ICalibrateEquipmentLoads>,
        modifiedLoadList: AgEventAPI.ICalibrateHarvestLoad[]
    ) {
        const { formatMessage } = this.props.intl;
        let numberOfLoads = 0;

        const equipmentBuckets = Array.from(equipmentGuidToHarvestLoadListMap.entries()).map(
            ([equipmentProfileGuid, item], equipmentIndex) => {
                const { customerFieldList, equipmentProfileName } = item;
                const loadList = customerFieldList.map((fielditem, fieldIndex) => {
                    const { customerName, fieldName, loadList } = fielditem;
                    const formattedFieldName = fieldName.replace(" -", ", ");
                    numberOfLoads += loadList.length;
                    const Checkboxlist = loadList.map((loadItem, loadIndex) => {
                        const {
                            agEventGuid,
                            averageCalibratedDryYield,
                            averageCalibratedPercent,
                            averageCalibratedWetYield,
                            totalDryYield,
                            averageDryYield,
                            averagePercent,
                            averageWetYield,
                            cropName,
                            cropPurposeName,
                            cropPurposeGuid,
                            eventDate,
                            eventName,
                            lintMode,
                            loadArea,
                            loadName,
                            yieldIaGuid,
                            yieldUnitName,
                        } = loadItem;
                        const checkBoxCSS = this.isLoadCalibrated(loadItem)
                            ? "calibrated"
                            : "unCalibrated";
                        const modifiedItem: AgEventAPI.ICalibrateHarvestLoad = {
                            agEventGuid: agEventGuid,
                            cropOptimalPercent: undefined,
                            equipmentProfileGuid: equipmentProfileGuid,
                            loadName: loadName,
                            eventDate,
                            eventName,
                            averageCalibratedDryYield: Number(averageCalibratedDryYield),
                            averageCalibratedPercent: Number(averageCalibratedPercent),
                            averageCalibratedWetYield: Number(averageCalibratedWetYield),
                            averageDryYield: Number(averageDryYield),
                            averagePercent: Number(averagePercent),
                            averageWetYield: Number(averageWetYield),
                            cropName,
                            cropPurposeName,
                            cropPurposeGuid,
                            lintMode,
                            loadArea: Number(loadArea),
                            totalDryYield: Number(totalDryYield),
                            yieldIaGuid,
                            yieldUnitName,
                        };
                        return (
                            <Checkbox
                                className={checkBoxCSS}
                                key={`${equipmentIndex}-${fieldIndex}-${loadIndex}-checkbox`}
                                label={
                                    eventName.length > 0
                                        ? `${loadName} - ${eventName}`
                                        : `${loadName}`
                                }
                                value={Boolean(
                                    modifiedLoadList.find((item) => {
                                        return (
                                            item.equipmentProfileGuid === equipmentProfileGuid &&
                                            item.agEventGuid === agEventGuid &&
                                            item.loadName === loadName &&
                                            item.eventName === eventName
                                        );
                                    })
                                )}
                                onChange={(_e, value) =>
                                    this.updateCheckedLoadList(modifiedItem, value)
                                }
                                testId={`yield-calibration-load-checkbox-${equipmentIndex}-${fieldIndex}-${loadIndex}`}
                            />
                        );
                    });
                    return (
                        <Bucket
                            showSymbol={false}
                            title={`${customerName}, ${formattedFieldName}`}
                            isCollapsible={false}
                            isExpanded
                            key={`${equipmentIndex}-${fieldIndex}-list`}
                            className="customer-field-bucket"
                            testId={`yield-calibration-customer-field-bucket-${equipmentIndex}-${fieldIndex}`}
                        >
                            <BucketHeader className="customer-field-name">
                                {`${customerName}, ${formattedFieldName} - ${loadList[0].yieldUnitName}`}
                            </BucketHeader>
                            {Checkboxlist}
                        </Bucket>
                    );
                });
                const someAreSelected = modifiedLoadList.some(
                    (l) => l.equipmentProfileGuid === equipmentProfileGuid
                );
                const allAreSelected = customerFieldList.every((cf) => {
                    return cf.loadList.every((l) => {
                        return (
                            l.equipmentProfileGuid !== equipmentProfileGuid ||
                            modifiedLoadList.some((item) => {
                                return (
                                    item.equipmentProfileGuid === l.equipmentProfileGuid &&
                                    item.agEventGuid === l.agEventGuid &&
                                    item.loadName === l.loadName &&
                                    item.eventName === l.eventName
                                );
                            })
                        );
                    });
                });

                return (
                    <Bucket
                        showSymbol={false}
                        isCollapsible={false}
                        isExpanded
                        key={`${equipmentIndex}-list`}
                        testId={`yield-calibration-equipment-bucket-${equipmentIndex}`}
                    >
                        <BucketHeader className="equipment-profile-name">
                            <Checkbox
                                label={
                                    equipmentProfileName ||
                                    formatMessage(messages.yieldCalibrationUnknown)
                                }
                                value={someAreSelected}
                                someChecked={someAreSelected && !allAreSelected}
                                onChange={(_e, value) =>
                                    this.toggleSelectAll(value, equipmentProfileGuid)
                                }
                                testId={`yield-calibration-equipment-checkbox-${equipmentIndex}`}
                            />
                        </BucketHeader>
                        {loadList}
                    </Bucket>
                );
            }
        );
        return (
            <Bucket
                className="select-load-panel"
                showSymbol={false}
                isCollapsible={false}
                isExpanded
                key="equipment-field-load-list"
                testId="yield-calibration-select-load-bucket"
            >
                <BucketHeader className="no-bucket-header"></BucketHeader>
                <Checkbox
                    className="yield-calibration-all-checkbox"
                    label={"All"}
                    value={modifiedLoadList.length > 0}
                    someChecked={
                        modifiedLoadList.length > 0 && modifiedLoadList.length < numberOfLoads
                    }
                    onChange={(_e, value) => this.toggleSelectAll(value)}
                    testId="yield-calibration-all-loads-checkbox"
                />

                {equipmentBuckets}
            </Bucket>
        );
    }

    private isLoadCalibrated(loadItem: AgEventAPI.ICalibrateHarvestLoad) {
        const { formatNumber } = this.props.intl;
        const {
            averageDryYield,
            averageCalibratedDryYield,
            averageWetYield,
            averageCalibratedWetYield,
            averagePercent,
            averageCalibratedPercent,
        } = loadItem;
        const isDryYieldCalibrated =
            averageCalibratedDryYield &&
            formatNumber(averageDryYield, intlConfig.numberFormatOptions) !==
                formatNumber(averageCalibratedDryYield, intlConfig.numberFormatOptions);
        const isWetYieldCalibrated =
            averageCalibratedWetYield &&
            formatNumber(averageWetYield, intlConfig.numberFormatOptions) !==
                formatNumber(averageCalibratedWetYield, intlConfig.numberFormatOptions);
        const isMoistureCalibrated =
            averageCalibratedPercent &&
            formatNumber(averagePercent, intlConfig.numberFormatOptions) !==
                formatNumber(averageCalibratedPercent, intlConfig.numberFormatOptions);
        return isDryYieldCalibrated || isWetYieldCalibrated || isMoistureCalibrated;
    }

    private resetYieldCalibration() {
        const { modifiedLoadList } = this.state;
        if (modifiedLoadList.length > 0 && modifiedLoadList.some((l) => this.isLoadCalibrated(l))) {
            this.setState({ confirmReset: true });
        } else {
            this.setState({ resetInvalid: true });
        }
    }

    private updateCheckedLoadList(loadItem: AgEventAPI.ICalibrateHarvestLoad, value: boolean) {
        const tmpList = [...this.state.modifiedLoadList];
        const cropIsMatch = !tmpList.some((load) => load.cropName !== loadItem.cropName);
        const cropPurposeIsMatch = this.isCropPurposeCompatible(loadItem, tmpList);
        const yieldUnitIsMatch = !tmpList.some(
            (load) => load.yieldUnitName !== loadItem.yieldUnitName
        );
        let isMatch = true;

        if (!(tmpList.length === 0 || (cropIsMatch && yieldUnitIsMatch && cropPurposeIsMatch))) {
            isMatch = false;
        }
        if (value && isMatch) {
            tmpList.push(loadItem);
        } else {
            const idx = tmpList.findIndex((item) => {
                return (
                    item.equipmentProfileGuid === loadItem.equipmentProfileGuid &&
                    item.agEventGuid === loadItem.agEventGuid &&
                    item.loadName === loadItem.loadName &&
                    item.eventName === loadItem.eventName
                );
            });
            if (idx > -1) {
                tmpList.splice(idx, 1);
            }
        }
        this.setState({
            modifiedLoadList: tmpList,
            errorsToDisplay: this.getErrorCodes(cropIsMatch, cropPurposeIsMatch, yieldUnitIsMatch),
        });
    }

    private getErrorCodes(
        cropIsMatch: boolean,
        cropPurposeIsMatch: boolean,
        yieldUnitIsMatch: boolean
    ): number[] {
        const errorCodes = [];
        if (!cropIsMatch) errorCodes.push(1);
        if (!yieldUnitIsMatch) errorCodes.push(2);
        if (!cropPurposeIsMatch) errorCodes.push(3);
        return errorCodes;
    }

    private isValidSelection(
        loadItem: AgEventAPI.ICalibrateHarvestLoad,
        tmpList: AgEventAPI.ICalibrateHarvestLoad[]
    ) {
        const cropIsMatch = !tmpList.some((load) => load.cropName !== loadItem.cropName);
        const yieldUnitIsMatch = !tmpList.some(
            (load) => load.yieldUnitName !== loadItem.yieldUnitName
        );
        let isCropUnitMatch = true;

        if (!(tmpList.length === 0 || (cropIsMatch && yieldUnitIsMatch))) {
            isCropUnitMatch = false;
        }

        return !(!cropIsMatch || !yieldUnitIsMatch || !isCropUnitMatch); // is valid
    }

    private onResetConfirmClick(confirm: boolean) {
        const { equipmentGuidToHarvestLoadListMap, setEquipmentGuidToHarvestLoadListMap } =
            this.props;
        const { modifiedLoadList, requests } = this.state;
        this.setState({ confirmReset: false });
        if (!confirm) {
            return;
        }

        const selectedLoads: AgEventAPI.ICalibrateHarvestLoad[] = modifiedLoadList.map(
            (loadItem) => {
                return {
                    agEventGuid: loadItem.agEventGuid,
                    equipmentProfileGuid: loadItem.equipmentProfileGuid,
                    loadName: loadItem.loadName,
                    loadArea: null,
                    eventName: null,
                    eventDate: null,
                    cropName: null,
                    cropPurposeName: null,
                    cropPurposeGuid: null,
                    cropOptimalPercent: null,
                    lintMode: null,
                    averageDryYield: null,
                    averageWetYield: null,
                    averagePercent: null,
                    totalDryYield: null,
                    yieldIaGuid: null,
                    yieldUnitName: null,
                    averageCalibratedDryYield: null,
                    averageCalibratedWetYield: null,
                    averageCalibratedPercent: null,
                };
            }
        );

        // Go through and mangle the equipmentGuidToHarvestLoadListMap such that it looks like the aftermath of the requested action
        const newEquipmentGuidToHarvestLoadListMap = new Map(equipmentGuidToHarvestLoadListMap);
        for (const [epg, item] of equipmentGuidToHarvestLoadListMap) {
            const { customerFieldList } = item;
            const newCustomerFieldList = [];

            for (const fieldItem of customerFieldList) {
                const newLoadList = [];

                for (const loadItem of fieldItem.loadList) {
                    const isMatch = selectedLoads.some(
                        (l) =>
                            l.agEventGuid === loadItem.agEventGuid &&
                            l.equipmentProfileGuid === loadItem.equipmentProfileGuid &&
                            l.loadName === loadItem.loadName
                    );
                    const resetItem = {
                        ...loadItem,
                        averageCalibratedDryYield: loadItem.averageDryYield,
                        averageCalibratedPercent: loadItem.averagePercent,
                        averageCalibratedWetYield: loadItem.averageWetYield,
                        totalDryYield: loadItem.averageDryYield * loadItem.loadArea,
                    };
                    newLoadList.push(isMatch ? resetItem : loadItem);
                }
                newCustomerFieldList.push({
                    ...fieldItem,
                    loadList: newLoadList,
                });
            }
            newEquipmentGuidToHarvestLoadListMap.set(epg, {
                ...item,
                customerFieldList: newCustomerFieldList,
            });
        }

        this.setState(
            {
                modifiedLoadList: [],
                requests: [...requests, { mode: RequestType.Reset, loads: selectedLoads }],
            },
            () => {
                setEquipmentGuidToHarvestLoadListMap(newEquipmentGuidToHarvestLoadListMap);
            }
        );
    }

    private onClose() {
        this.props.clearYieldCalibrationLoadList();
        this.props.onClose();
    }

    private toggleSelectAll(val: boolean, equipmentProfileGuid: string = null) {
        const { modifiedLoadList } = this.state;
        const { equipmentGuidToHarvestLoadListMap } = this.props;
        const tmpList: AgEventAPI.ICalibrateHarvestLoad[] = [];
        let cropsMatch = true;
        let cropPurposeMatch = true;
        let unitsMatch = true;

        if (equipmentProfileGuid == null) {
            if (!val && modifiedLoadList.length > 0) {
                this.setState({
                    modifiedLoadList: [],
                });
            } else {
                for (const [, item] of equipmentGuidToHarvestLoadListMap) {
                    const { customerFieldList } = item;
                    for (const fieldItem of customerFieldList) {
                        for (const loadItem of fieldItem.loadList) {
                            const {
                                agEventGuid,
                                averageCalibratedDryYield,
                                averageCalibratedPercent,
                                averageCalibratedWetYield,
                                totalDryYield,
                                averageDryYield,
                                averagePercent,
                                averageWetYield,
                                cropName,
                                cropPurposeName,
                                cropPurposeGuid,
                                eventDate,
                                eventName,
                                lintMode,
                                loadArea,
                                loadName,
                                yieldIaGuid,
                                yieldUnitName,
                            } = loadItem;
                            const modifiedItem: AgEventAPI.ICalibrateHarvestLoad = {
                                agEventGuid: agEventGuid,
                                cropOptimalPercent: undefined,
                                equipmentProfileGuid: loadItem.equipmentProfileGuid,
                                loadName: loadName,
                                eventDate,
                                eventName,
                                averageCalibratedDryYield: Number(averageCalibratedDryYield),
                                averageCalibratedPercent: Number(averageCalibratedPercent),
                                averageCalibratedWetYield: Number(averageCalibratedWetYield),
                                averageDryYield: Number(averageDryYield),
                                averagePercent: Number(averagePercent),
                                averageWetYield: Number(averageWetYield),
                                cropName,
                                cropPurposeName,
                                cropPurposeGuid,
                                lintMode,
                                loadArea: Number(loadArea),
                                totalDryYield: Number(totalDryYield),
                                yieldIaGuid,
                                yieldUnitName,
                            };

                            const isCropUnitValid = this.isValidSelection(modifiedItem, tmpList);
                            const isPurposeValid = this.isCropPurposeCompatible(
                                modifiedItem,
                                tmpList
                            );
                            if (isCropUnitValid && isPurposeValid) {
                                tmpList.push(modifiedItem);
                            } else {
                                cropsMatch =
                                    cropsMatch && modifiedItem.cropName === tmpList[0]?.cropName;
                                cropPurposeMatch = cropPurposeMatch && isPurposeValid;
                                unitsMatch =
                                    unitsMatch &&
                                    modifiedItem.yieldUnitName === tmpList[0]?.yieldUnitName;
                            }
                        }
                    }
                }

                this.setState({
                    modifiedLoadList: tmpList,
                });
            }
        } else {
            if (
                !val &&
                modifiedLoadList.some((l) => l.equipmentProfileGuid === equipmentProfileGuid)
            ) {
                this.setState({
                    modifiedLoadList: modifiedLoadList.filter(
                        (l) => l.equipmentProfileGuid !== equipmentProfileGuid
                    ),
                });
            } else {
                tmpList.push(...modifiedLoadList);
                for (const [epg, item] of equipmentGuidToHarvestLoadListMap) {
                    if (epg !== equipmentProfileGuid) {
                        continue;
                    }
                    const { customerFieldList } = item;
                    for (const fieldItem of customerFieldList) {
                        for (const loadItem of fieldItem.loadList) {
                            const {
                                agEventGuid,
                                averageCalibratedDryYield,
                                averageCalibratedPercent,
                                averageCalibratedWetYield,
                                totalDryYield,
                                averageDryYield,
                                averagePercent,
                                averageWetYield,
                                cropName,
                                cropPurposeName,
                                cropPurposeGuid,
                                eventDate,
                                eventName,
                                lintMode,
                                loadArea,
                                loadName,
                                yieldIaGuid,
                                yieldUnitName,
                            } = loadItem;
                            const modifiedItem: AgEventAPI.ICalibrateHarvestLoad = {
                                agEventGuid: agEventGuid,
                                cropOptimalPercent: undefined,
                                equipmentProfileGuid: equipmentProfileGuid,
                                loadName: loadName,
                                eventDate,
                                eventName,
                                averageCalibratedDryYield: Number(averageCalibratedDryYield),
                                averageCalibratedPercent: Number(averageCalibratedPercent),
                                averageCalibratedWetYield: Number(averageCalibratedWetYield),
                                averageDryYield: Number(averageDryYield),
                                averagePercent: Number(averagePercent),
                                averageWetYield: Number(averageWetYield),
                                cropName,
                                cropPurposeName,
                                cropPurposeGuid,
                                lintMode,
                                loadArea: Number(loadArea),
                                totalDryYield: Number(totalDryYield),
                                yieldIaGuid,
                                yieldUnitName,
                            };

                            const isCropUnitValid = this.isValidSelection(modifiedItem, tmpList);
                            const isPurposeValid = this.isCropPurposeCompatible(
                                modifiedItem,
                                tmpList
                            );
                            if (isCropUnitValid && isPurposeValid) {
                                tmpList.push(modifiedItem);
                            } else {
                                cropsMatch =
                                    cropsMatch && modifiedItem.cropName === tmpList[0]?.cropName;
                                cropPurposeMatch = cropPurposeMatch && isPurposeValid;
                                unitsMatch =
                                    unitsMatch &&
                                    modifiedItem.yieldUnitName === tmpList[0]?.yieldUnitName;
                            }
                        }
                    }
                }
                this.setState({
                    modifiedLoadList: tmpList,
                });
            }
        }

        this.setState({
            errorsToDisplay: this.getErrorCodes(cropsMatch, cropPurposeMatch, unitsMatch),
        });
    }

    private getDisplayError(formatMessage: IntlShape["formatMessage"]): JSX.Element {
        const { errorsToDisplay } = this.state;
        if (errorsToDisplay.length === 0) {
            return null;
        }
        const parts: JSX.Element[] = [];
        if (errorsToDisplay.includes(1))
            parts.push(<span key="crop">{formatMessage(messages.cropMatchError)}</span>);
        if (errorsToDisplay.includes(2))
            parts.push(<span key="unit">{formatMessage(messages.unitMatchError)}</span>);
        if (errorsToDisplay.includes(3))
            parts.push(<span key="purpose">{formatMessage(messages.cropPurposeMatchError)}</span>);
        return (
            <React.Fragment>
                {parts.map((el, i) => (
                    <React.Fragment key={el.key}>
                        {i > 0 && <br />}
                        {el}
                    </React.Fragment>
                ))}
            </React.Fragment>
        );
    }

    componentDidUpdate(prevProps: IYieldCalibrationModalProps) {
        if (
            this.props.equipmentGuidToHarvestLoadListMap !==
            prevProps.equipmentGuidToHarvestLoadListMap
        ) {
            const newModifiedLoadList = this.state.modifiedLoadList.map((load) => {
                const equipmentRecord = this.props.equipmentGuidToHarvestLoadListMap.get(
                    load.equipmentProfileGuid
                );
                if (equipmentRecord == null) {
                    return null;
                } else {
                    const customerFieldRecord = equipmentRecord.customerFieldList.find(
                        (customerField) => {
                            return customerField.loadList.some(
                                (fieldLoad) => fieldLoad.agEventGuid === load.agEventGuid
                            );
                        }
                    );
                    const newLoad = customerFieldRecord.loadList.find((fieldLoad) => {
                        return (
                            fieldLoad.agEventGuid === load.agEventGuid &&
                            fieldLoad.loadName === load.loadName &&
                            fieldLoad.eventName === load.eventName
                        );
                    });
                    return {
                        ...load,
                        ...newLoad,
                    };
                }
            });
            if (newModifiedLoadList[0] != null) {
                this.setState({
                    modifiedLoadList: newModifiedLoadList,
                    moisture: this.props.passedYieldCalibrationValidation
                        ? -1
                        : this.state.moisture,
                    yieldAmount: this.props.passedYieldCalibrationValidation
                        ? -1
                        : this.state.yieldAmount,
                });
            }
        }
        if (
            this.props.yieldCalibrationPicklist.length > 0 &&
            this.props.yieldCalibrationPicklist !== prevProps.yieldCalibrationPicklist &&
            (this.props.passedYieldCalibrationValidation ||
                this.state.yieldCalibrationAdjustId == null)
        ) {
            this.setState({
                yieldCalibrationAdjustId: AVG_DRY_YIELD,
            });
        }
    }

    async componentDidMount() {
        this.props.fetchPicklistData({
            [PICKLIST_CROP_PURPOSE]: getPickListCode(PICKLIST_CROP_PURPOSE),
        });

        const title = await this.getModalTitle();

        this.setState({
            title,
        });
    }

    render() {
        const { isOpen, isLoading, equipmentGuidToHarvestLoadListMap, yieldCalibrationPicklist } =
            this.props;
        const { formatMessage, formatNumber } = this.props.intl;
        const {
            confirmReset,
            modifiedLoadList,
            moisture,
            resetInvalid,
            requests,
            title,
            yieldAmount,
            yieldCalibrationAdjustId,
        } = this.state;
        const errorTextToDisplay = this.getDisplayError(formatMessage);
        const moistureSet = moisture !== null && moisture > -1;
        const yieldSet = yieldAmount !== null && yieldAmount > -1;
        const isCotton = modifiedLoadList.length > 0 && modifiedLoadList[0].lintMode;

        const actionList = [
            {
                action: formatMessage(messages.applyYieldCalibration),
                actionDisabled:
                    modifiedLoadList.length === 0 ||
                    (!moistureSet && (!yieldSet || yieldCalibrationAdjustId === null)),
                onAction: () => this.applyYieldCalibration(),
            },
            {
                action: formatMessage(messages.saveText),
                actionDisabled: requests.length === 0,
                onAction: () => this.saveYieldCalibration(),
            },
        ];
        const selectableLoads =
            equipmentGuidToHarvestLoadListMap.size > 0
                ? this.createSelectedLoadsCheckboxes(
                      equipmentGuidToHarvestLoadListMap,
                      modifiedLoadList
                  )
                : "";
        const recordsData = this.calculateYieldAverages(modifiedLoadList);
        const { calculatedYieldAveragesRecords, cropName, cropPurposeName, yieldUnitName } =
            recordsData;

        const picklistOptions: ISelectOption<number>[] =
            !yieldCalibrationPicklist || yieldCalibrationPicklist.length === 0
                ? []
                : yieldCalibrationPicklist.map((item) => ({
                      value: Number(item.id),
                      label: !isCotton ? item.value : item.value.replace("Dry", "Lint"),
                  }));

        const cropPurposeText = cropPurposeName ? ` (${cropPurposeName})` : "";
        const crop = `${formatMessage(
            messages.yieldCalibrationCrop
        )} : ${cropName} ${cropPurposeText}`;
        const totalYield = `${formatMessage(messages.yieldCalibrationTotalYield)} : ${formatNumber(
            recordsData.totalYieldAmount,
            intlConfig.wholeNumberFormatOptions
        )} ${yieldUnitName?.replace(PER_ACRE, "")}`;
        const totalArea = `${formatMessage(messages.yieldCalibrationTotalArea)} : ${formatNumber(
            recordsData.totalAreaAmount,
            intlConfig.numberFormatOptions
        )} ac`;

        const columns = [
            { dataKey: "label", label: "", width: 150 },
            {
                dataKey: "original",
                label: formatMessage(messages.yieldCalibrationOriginal),
                width: 150,
            },
            {
                dataKey: "calibrated",
                label: formatMessage(messages.yieldCalibrationCalibrated),
                width: 150,
            },
        ];

        return (
            <DialogBox
                className="yield-calibration-dialog"
                draggable
                footerType={DialogBoxFooterType.MULTI_ACTION_CANCEL}
                multiActionList={actionList}
                forceOverflow
                isOpen={isOpen}
                unrestricted
                onClose={() => this.onClose()}
                title={title}
                testId="yield-calibration-modal"
            >
                <Bucket
                    showSymbol={false}
                    isCollapsible={false}
                    isExpanded
                    className="yield-calibration-select-loads"
                    testId="yield-calibration-select-loads-bucket"
                >
                    <BucketHeader className="yield-calibration-title">
                        <span>{formatMessage(messages.yieldCalibrationSelectLoads)}</span>
                        <NoLink
                            className="reset-link"
                            label={formatMessage(messages.resetYieldCalibration)}
                            onClick={() => this.resetYieldCalibration()}
                        />
                    </BucketHeader>
                    {selectableLoads}
                    <div className="calibrated-legend">
                        <div className="calibrated-legend-symbol"></div>
                        <div className="calibrated-legend-label">
                            {formatMessage(messages.yieldCalibrationCalibratedLoads)}
                        </div>
                    </div>
                </Bucket>
                <DialogBox
                    footerType={DialogBoxFooterType.YES_NO}
                    isOpen={confirmReset}
                    onAction={() => this.onResetConfirmClick(true)}
                    onClose={() => this.onResetConfirmClick(false)}
                    title={formatMessage(messages.resetYieldConfirmationTitle)}
                    testId="yield-calibration-confirm-reset-modal"
                >
                    {formatMessage(messages.resetYieldConfirmationMessage)}
                </DialogBox>
                <DialogBox
                    footerType={DialogBoxFooterType.NONE}
                    isOpen={resetInvalid}
                    onClose={() => this.setState({ resetInvalid: false })}
                    title={formatMessage(messages.resetYieldConfirmationTitle)}
                    testId="yield-calibration-reset-invalid-modal"
                >
                    {formatMessage(messages.resetYieldInvalidMessage)}
                </DialogBox>
                <Bucket
                    showSymbol={false}
                    isCollapsible={false}
                    isExpanded
                    className="yield-calibration-averages"
                    testId="yield-calibration-averages-bucket"
                >
                    <BucketHeader className="yield-calibration-title">
                        {formatMessage(messages.yieldCalibrationAverages)}
                    </BucketHeader>
                    <Bucket
                        showSymbol={false}
                        isCollapsible={false}
                        isExpanded
                        className="yield-calibration-averages-tables"
                        testId="yield-calibration-averages-tables-bucket"
                    >
                        <div className="crop-total-yield-label">{crop}</div>
                        <div className="crop-total-yield-label">{totalYield}</div>
                        <div className="crop-total-yield-label">{totalArea}</div>
                        <SimpleTable
                            sortDirection={"ASC"}
                            height={250}
                            width={400}
                            className="yield-calibration-results-table"
                            headerHeight={30}
                            rowHeight={25}
                            columns={columns}
                            records={calculatedYieldAveragesRecords as any}
                        />
                    </Bucket>
                    <Bucket
                        showSymbol={false}
                        isCollapsible={false}
                        isExpanded
                        className="yield-calibration-averages-input"
                        testId="yield-calibration-averages-input-bucket"
                    >
                        <RadioButtonGroup
                            selectedValue={this.state.isCalibrateToYield}
                            afterOnChange={(v) => {
                                this.setState({
                                    isCalibrateToYield: v,
                                    moisture: v ? null : this.state.moisture,
                                    isOverrideMoisture: v ? null : this.state.isOverrideMoisture,
                                    yieldCalibrationAdjustId: v
                                        ? this.state.yieldCalibrationAdjustId
                                        : null,
                                    yieldAmount: v ? this.state.yieldAmount : null,
                                });
                            }}
                        >
                            <RadioButton
                                value={false}
                                label={
                                    <div className="input-row">
                                        <div className="label">
                                            {formatMessage(
                                                !isCotton
                                                    ? messages.yieldCalibrationMoistureLabel
                                                    : messages.yieldCalibrationLintPctLabel
                                            )}
                                        </div>
                                        <NumericInput
                                            disabled={Boolean(this.state.isCalibrateToYield)}
                                            scale={2}
                                            precision={8}
                                            className="yield-calibration-form-input"
                                            placeholderText={
                                                !isCotton
                                                    ? formatMessage(
                                                          messages.yieldCalibrationMoisture
                                                      )
                                                    : formatMessage(messages.yieldCalibrationLint)
                                            }
                                            onChange={(value) => {
                                                this.setState({
                                                    moisture: Number(value),
                                                });
                                            }}
                                            value={moistureSet ? moisture : ""}
                                            testId="yield-calibration-moisture-input"
                                        />
                                        <div className="label no-grow">%</div>
                                        {isCotton ? null : (
                                            <Checkbox
                                                className="override-checkbox"
                                                value={this.state.isOverrideMoisture}
                                                disabled={Boolean(this.state.isCalibrateToYield)}
                                                onChange={(_e, val) =>
                                                    this.setState({
                                                        isOverrideMoisture: val,
                                                    })
                                                }
                                                label={formatMessage(messages.overrideMoistureText)}
                                                testId="yield-calibration-override-moisture-checkbox"
                                            />
                                        )}
                                    </div>
                                }
                                testId="yield-calibration-calibrate-to-moisture-radio"
                            />
                            <RadioButton
                                value={true}
                                label={
                                    <div className="input-row">
                                        <div className="label">
                                            {formatMessage(messages.yieldCalibrationYieldLabel)}
                                        </div>
                                        <NumericInput
                                            disabled={!this.state.isCalibrateToYield}
                                            scale={2}
                                            precision={15}
                                            className="yield-calibration-form-input"
                                            placeholderText={formatMessage(
                                                messages.yieldCalibrationYield
                                            )}
                                            onChange={(value) => {
                                                this.setState({
                                                    yieldAmount: Number(value),
                                                });
                                            }}
                                            value={yieldSet ? yieldAmount : ""}
                                            testId="yield-calibration-yield-amount-input"
                                        />
                                        <SelectInput
                                            disabled={!this.state.isCalibrateToYield}
                                            required
                                            clearable={false}
                                            options={picklistOptions}
                                            onChange={(v) =>
                                                this.setState({
                                                    yieldCalibrationAdjustId: v as number,
                                                })
                                            }
                                            placeholderText={formatMessage(
                                                messages.yieldCalibrationAdjustType
                                            )}
                                            value={yieldCalibrationAdjustId}
                                            testId="yield-calibration-adjust-type-select"
                                        />
                                    </div>
                                }
                                testId="yield-calibration-calibrate-to-yield-radio"
                            />
                        </RadioButtonGroup>
                    </Bucket>
                </Bucket>
                {!isLoading ? null : <Loader />}
                <DialogBox
                    draggable
                    footerType={DialogBoxFooterType.NONE}
                    isOpen={errorTextToDisplay !== null}
                    onClose={() => this.setState({ errorsToDisplay: [] })}
                    title={""}
                    testId="yield-calibration-errors-modal"
                >
                    {errorTextToDisplay}
                </DialogBox>
                <DialogBox
                    draggable
                    footerType={DialogBoxFooterType.NONE}
                    isOpen={this.props.yieldCalibrationHasError}
                    onClose={() => this.props.clearYieldCalibrationError()}
                    title={""}
                    testId="yield-calibration-error-modal"
                >
                    {formatMessage(messages.yieldWeightError)}
                </DialogBox>
            </DialogBox>
        );
    }
}

const mapDispatchToProps = (dispatch) => ({
    onApiError: (err, message) => dispatch(notificationActions.apiCallError(err, null, message)),
    saveYieldCalibration: (yieldCalibrationModel, agEventGuidList) =>
        dispatch(eventActions.applyYieldCalibration(yieldCalibrationModel, agEventGuidList)),
    onClose: () => dispatch(eventListActions.showHideYieldCalibrationModal(false)),
    clearYieldCalibrationError: () => dispatch(eventActions.clearYieldCalibrationError()),
    clearYieldCalibrationLoadList: () => dispatch(eventActions.clearYieldCalibrationLoadList()),
    setEquipmentGuidToHarvestLoadListMap: (equipmentGuidToHarvestLoadListMap) =>
        dispatch(eventActions.setEquipmentHarvestLoadMap(equipmentGuidToHarvestLoadListMap)),
    fetchPicklistData: (picklists: Record<string, number>) =>
        dispatch(picklistActions.fetchPicklistData(picklists)),
});

const mapStateToProps = (state) => {
    const cropPurposeOptions = picklistSelectors.getPicklistOptionsFromCode(
        state,
        getPickListCode(PICKLIST_CROP_PURPOSE)
    ) as { value: string; label: string }[];

    const wholeCropPurposeGuids = new Set(
        cropPurposeOptions
            .filter((opt) => WHOLE_CROP_PURPOSE_NAMES.has(opt.label))
            .map((opt) => opt.value)
    );

    return {
        equipmentGuidToHarvestLoadListMap: getEquipmentGuidToHarvestLoadListMap(state),
        eventSummary: getEventSummary(state),
        fieldEventMap: getFieldGuidToSelectedEventGuidSetMap(state),
        isLoading: getYieldCalibrationLoading(state),
        passedYieldCalibrationValidation: getYieldCalibrationPassedValidation(state),
        userGuid: getTheUserGuid(state),
        yieldCalibrationPicklist: getYieldCalibrationOptions(state),
        yieldCalibrationHasError: getYieldCalibrationErrorState(state),
        wholeCropPurposeGuids,
    };
};

export const YieldCalibrationModal = connect(
    mapStateToProps,
    mapDispatchToProps
)(injectIntl(YieldCalibrationModal_));
